import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  getActasCompromiso,
  crearActaCompromiso,
  actualizarActaCompromiso,
  eliminarActaCompromiso
} from '../services/actasCompromisoApi';

const API_URL = process.env.REACT_APP_API_URL;

const getEmpleadosHorarios = async () => {
  const res = await fetch(`${API_URL}/api/horarios/empleados`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || 'Error obteniendo empleados');
  return data;
};

const getSedesHorarios = async () => {
  const res = await fetch(`${API_URL}/api/horarios/sedes`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || 'Error obteniendo sedes');
  return data;
};

const resolverAlcanceABAC = (usuario) => {
  const cargo = String(usuario?.cargo || '').toUpperCase();
  const esGlobal = cargo.includes('SUPER') || cargo.includes('GERENCIA') || cargo.includes('TALENTO HUMANO');
  return { esGlobal };
};

const MOTIVOS_LISTA = [
  'Puntualidad y asistencia',
  'Presentación personal / dotación',
  'Desempeño',
  'Servicio al cliente',
  'Caja / recaudo',
  'Cumplimiento de procedimientos SST',
  'Orden y aseo',
  'Convivencia',
  'Otro'
];

// ==========================================
// COMPONENTE INTERNO: PANEL DE FIRMA CANVAS
// ==========================================
const FirmaPad = ({ titulo, valorBase64, onGuardarFirma, onLimpiar }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    if (e.touches && e.touches.length > 0) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const { x, y } = getCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const dataUrl = canvasRef.current.toDataURL('image/png');
      onGuardarFirma(dataUrl);
    }
  };

  useEffect(() => {
    if (!valorBase64 && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  }, [valorBase64]);

  return (
    <div className="flex flex-col items-center bg-white p-3 rounded-2xl border border-slate-200 shadow-sm">
      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 text-center">{titulo}</span>
      {valorBase64 ? (
        <div className="relative w-full h-24 border-2 border-dashed border-emerald-200 rounded-xl bg-emerald-50/50 flex items-center justify-center overflow-hidden group">
          <img src={valorBase64} alt={`Firma ${titulo}`} className="max-h-full object-contain mix-blend-multiply" />
          <button type="button" onClick={onLimpiar} className="absolute top-1 right-1 bg-white border border-rose-200 text-rose-600 rounded-lg p-1 px-2 text-[10px] font-black shadow-md hover:bg-rose-50 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity">
            Borrar
          </button>
        </div>
      ) : (
        <div className="w-full h-24 border-2 border-dashed border-slate-300 rounded-xl bg-slate-50 relative touch-none cursor-crosshair hover:border-blue-400 transition-colors">
          <canvas
            ref={canvasRef}
            width={220}
            height={96}
            className="w-full h-full rounded-xl"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
          <span className="absolute bottom-2 right-2 text-[10px] text-slate-400 pointer-events-none font-black opacity-50">Firme aquí</span>
        </div>
      )}
    </div>
  );
};

// ==========================================
// VISTA PRINCIPAL ACTAS COMPROMISO
// ==========================================
export default function ActasCompromisoView({ usuario, permisosCRUD, onBack }) {
  const [actas, setActas] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [cargando, setCargando] = useState(true);

  const [modalAbierto, setModalAbierto] = useState(false);
  const [actaEditando, setActaEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState(null);

  const [form, setForm] = useState({
    id_empleado: '',
    sede_id: '',
    jefe_inmediato: usuario?.nombres || usuario?.nombre || '',
    responsable_seguimiento: '',
    fecha_seguimiento_inicial: new Date().toISOString().split('T')[0],
    motivos: {},
    hechos_antecedentes: '',
    fechas_horas_soportes: '',
    verificacion_previa: { perfil_cargo: 'No aplica', manual_funciones: 'No aplica', induccion: 'No aplica', reinduccion: 'No aplica', procedimiento_especifico: 'No aplica', dotacion: 'No aplica' },
    soportes_socializacion: '',
    version_trabajador: '',
    compromisos_trabajador: [{ accion: '', indicador: '', plazo: '' }],
    compromisos_empresa: { reinduccion: false, acompanamiento: false, resolver_dudas: false, verificar_recursos: false, seguimiento: false, otro_texto: '' },
    seguimientos: [
      { etapa: 1, fecha: '', aspecto: '', resultado: '', responsable: '' },
      { etapa: 2, fecha: '', aspecto: '', resultado: '', responsable: '' },
      { etapa: 3, fecha: '', aspecto: '', resultado: '', responsable: '' }
    ],
    resultado_final: 'EN_SEGUIMIENTO',
    observaciones_finales: '',
    firmas: { trabajador: '', jefe: '', th: '', testigo1: '' }
  });

  const mostrarToast = useCallback((mensaje, tipo = 'success') => {
    setToast({ mensaje, tipo });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    try {
      const [resActas, resEmp, resSedes] = await Promise.all([
        getActasCompromiso().catch(() => []),
        getEmpleadosHorarios().catch(() => ({ success: false, data: [] })),
        getSedesHorarios().catch(() => ({ success: false, data: [] }))
      ]);
      
      const actasData = resActas?.data ? resActas.data : (Array.isArray(resActas) ? resActas : []);
      setActas(actasData);
      
      if (resEmp && resEmp.success) setEmpleados(resEmp.data || []);
      if (resSedes && resSedes.success) setSedes(resSedes.data || []);
    } catch (err) {
      mostrarToast('Error cargando datos: ' + err.message, 'error');
    } finally {
      setCargando(false);
    }
  }, [mostrarToast]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const alcanceABAC = useMemo(() => resolverAlcanceABAC(usuario), [usuario]);

  // 1. Filtrado CORREGIDO para Empleados (Lista Desplegable)
  const empleadosPermitidos = useMemo(() => {
    const activos = empleados.filter(e => String(e.estado || 'ACTIVO').toUpperCase() === 'ACTIVO');

    if (!permisosCRUD || alcanceABAC.esGlobal || permisosCRUD.alcance === 'GLOBAL') {
      return activos;
    }

    const alcance = permisosCRUD.alcance || 'PROPIA';
    
    if (alcance === 'ESPECIFICA') {
      const permitidas = permisosCRUD.sedes_ids || [];
      return activos.filter(e => {
        const sedeDelEmp = sedes.find(s => String(s.id) === String(e.sede_id));
        return (
          permitidas.includes(String(e.sede_id)) || 
          (sedeDelEmp && permitidas.includes(String(sedeDelEmp.codigo_bodega)))
        );
      });
    }
    
    // ALCANCE PROPIA
    const sedeUser = String(usuario?.sede_id || '');
    return activos.filter(e => {
      const sedeDelEmp = sedes.find(s => String(s.id) === String(e.sede_id));
      return (
        String(e.sede_id) === sedeUser || 
        (sedeDelEmp && String(sedeDelEmp.codigo_bodega) === sedeUser)
      );
    });
  }, [empleados, permisosCRUD, alcanceABAC, sedes, usuario]);

  // 2. Filtrado CORREGIDO para Actas (Tabla Principal)
  const actasPermitidas = useMemo(() => {
    if (!permisosCRUD || alcanceABAC.esGlobal) return actas;

    const alcance = permisosCRUD.alcance || 'PROPIA';
    
    if (alcance === 'GLOBAL') return actas;
    
    if (alcance === 'ESPECIFICA') {
      const permitidas = permisosCRUD.sedes_ids || [];
      return actas.filter(a => {
        const sedeDelActa = sedes.find(s => String(s.id) === String(a.sede_id));
        return (
          permitidas.includes(String(a.sede_id)) || 
          (sedeDelActa && permitidas.includes(String(sedeDelActa.codigo_bodega)))
        );
      });
    }
    
    // ALCANCE PROPIA
    const sedeUser = String(usuario?.sede_id || '');
    return actas.filter(a => String(a.sede_id) === sedeUser);
  }, [actas, permisosCRUD, alcanceABAC, sedes, usuario]);

  const handleSelectEmpleado = (empId) => {
    const emp = empleados.find(e => String(e.id) === String(empId));
    let codigoBodega = '';
    
    if (emp && emp.sede_id) {
      const sedeEncontrada = sedes.find(s => String(s.id) === String(emp.sede_id));
      if (sedeEncontrada) {
        codigoBodega = sedeEncontrada.codigo_bodega;
      }
    }

    setForm(prev => ({
      ...prev,
      id_empleado: empId,
      sede_id: codigoBodega
    }));
  };

  const abrirModalCrear = () => {
    setActaEditando(null);
    setForm({
      id_empleado: '',
      sede_id: '',
      jefe_inmediato: usuario?.nombres || usuario?.nombre || '',
      responsable_seguimiento: '',
      fecha_seguimiento_inicial: new Date().toISOString().split('T')[0],
      motivos: {},
      hechos_antecedentes: '',
      fechas_horas_soportes: '',
      verificacion_previa: { perfil_cargo: 'No aplica', manual_funciones: 'No aplica', induccion: 'No aplica', reinduccion: 'No aplica', procedimiento_especifico: 'No aplica', dotacion: 'No aplica' },
      soportes_socializacion: '',
      version_trabajador: '',
      compromisos_trabajador: [{ accion: '', indicador: '', plazo: '' }],
      compromisos_empresa: { reinduccion: false, acompanamiento: false, resolver_dudas: false, verificar_recursos: false, seguimiento: false, otro_texto: '' },
      seguimientos: [
        { etapa: 1, fecha: '', aspecto: '', resultado: '', responsable: '' },
        { etapa: 2, fecha: '', aspecto: '', resultado: '', responsable: '' },
        { etapa: 3, fecha: '', aspecto: '', resultado: '', responsable: '' }
      ],
      resultado_final: 'EN_SEGUIMIENTO',
      observaciones_finales: '',
      firmas: { trabajador: '', jefe: '', th: '', testigo1: '' }
    });
    setModalAbierto(true);
  };

  const abrirModalEditar = (acta) => {
    setActaEditando(acta);
    
    const parseJSONSeguro = (campoBD, defaultVal) => {
      try { return typeof campoBD === 'string' ? JSON.parse(campoBD || 'null') || defaultVal : (campoBD || defaultVal); } 
      catch (e) { return defaultVal; }
    };

    setForm({
      id_empleado: acta.id_empleado,
      sede_id: acta.sede_id || '',
      jefe_inmediato: acta.jefe_inmediato || '',
      responsable_seguimiento: acta.responsable_seguimiento || '',
      fecha_seguimiento_inicial: acta.fecha_seguimiento_inicial ? String(acta.fecha_seguimiento_inicial).split('T')[0] : '',
      motivos: parseJSONSeguro(acta.motivos_json, {}),
      hechos_antecedentes: acta.hechos_antecedentes || '',
      fechas_horas_soportes: acta.fechas_horas_soportes || '',
      verificacion_previa: parseJSONSeguro(acta.verificacion_previa_json, { perfil_cargo: 'No aplica', manual_funciones: 'No aplica', induccion: 'No aplica', reinduccion: 'No aplica', procedimiento_especifico: 'No aplica', dotacion: 'No aplica' }),
      soportes_socializacion: acta.soportes_socializacion || '',
      version_trabajador: acta.version_trabajador || '',
      compromisos_trabajador: parseJSONSeguro(acta.compromisos_trabajador_json, []),
      compromisos_empresa: parseJSONSeguro(acta.compromisos_empresa_json, {}),
      seguimientos: parseJSONSeguro(acta.seguimientos_json, []),
      resultado_final: acta.resultado_final || 'EN_SEGUIMIENTO',
      observaciones_finales: acta.observaciones_finales || '',
      firmas: parseJSONSeguro(acta.firmas_json, { trabajador: '', jefe: '', th: '', testigo1: '' })
    });
    setModalAbierto(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.id_empleado) {
      mostrarToast('Selecciona un colaborador', 'error');
      return;
    }

    setGuardando(true);
    try {
      const dataAEnviar = {
        id_empleado: form.id_empleado,
        sede_id: form.sede_id || null,
        jefe_inmediato: form.jefe_inmediato,
        responsable_seguimiento: form.responsable_seguimiento,
        fecha_seguimiento_inicial: form.fecha_seguimiento_inicial || null,
        motivos_json: form.motivos,
        hechos_antecedentes: form.hechos_antecedentes,
        fechas_horas_soportes: form.fechas_horas_soportes,
        verificacion_previa_json: form.verificacion_previa,
        soportes_socializacion: form.soportes_socializacion,
        version_trabajador: form.version_trabajador,
        compromisos_trabajador_json: form.compromisos_trabajador,
        compromisos_empresa_json: form.compromisos_empresa,
        seguimientos_json: form.seguimientos,
        resultado_final: form.resultado_final,
        observaciones_finales: form.observaciones_finales,
        firmas_json: form.firmas,
        creado_por: usuario?.nombres || usuario?.nombre || 'Administrador'
      };

      if (actaEditando) {
        await actualizarActaCompromiso(actaEditando.id, dataAEnviar);
        mostrarToast('✅ Acta PF-TH-101 actualizada', 'success');
      } else {
        await crearActaCompromiso(dataAEnviar);
        mostrarToast('✅ Acta PF-TH-101 creada exitosamente', 'success');
      }

      setModalAbierto(false);
      cargarDatos();
    } catch (err) {
      mostrarToast('Error guardando acta: ' + err.message, 'error');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta acta de compromiso?')) return;
    try {
      await eliminarActaCompromiso(id);
      mostrarToast('Acta eliminada', 'success');
      cargarDatos();
    } catch (err) {
      mostrarToast('Error eliminando acta: ' + err.message, 'error');
    }
  };

  return (
    <div className="bg-white/40 backdrop-blur-[45px] rounded-[36px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] p-6 sm:p-10 max-w-7xl w-full mx-auto border border-white/80 animate-fade-in relative overflow-hidden h-[88vh] overflow-y-auto custom-scrollbar">
      {toast && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-2xl border backdrop-blur-md transition-all ${
          toast.tipo === 'error' ? 'bg-red-950/95 text-white border-red-500/40' : 'bg-slate-900/95 text-white border-emerald-500/40'
        }`}>
          <span className={`w-2.5 h-2.5 rounded-full ${toast.tipo === 'error' ? 'bg-red-400 animate-ping' : 'bg-emerald-400'}`} />
          <span className="text-xs font-black tracking-wide">{toast.mensaje}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-200/50 pb-6 mb-6 gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-indigo-50/80 rounded-2xl flex items-center justify-center text-3xl border border-indigo-100 shadow-sm">🤝</div>
          <div>
            <h2 className="text-3xl font-black text-slate-800 tracking-tight">Actas de Compromiso y Plan de Mejora</h2>
            <p className="text-xs font-bold text-slate-500 mt-1">Formato Oficial PF-TH-101 • Gestión y Control Documental</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {(permisosCRUD?.crear || alcanceABAC.esGlobal) && (
            <button onClick={abrirModalCrear} className="px-5 py-3 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-black text-xs shadow-md transition cursor-pointer">
               Nueva Acta PF-TH-101
            </button>
          )}
          <button onClick={onBack} className="w-14 h-14 bg-white hover:bg-slate-50 text-slate-700 flex items-center justify-center rounded-2xl transition-all shadow-sm border border-slate-200 cursor-pointer">
             Volver
          </button>
        </div>
      </div>

      <div className="bg-white/60 backdrop-blur-md rounded-2xl border border-white shadow-lg overflow-hidden">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-900 text-white uppercase font-black">
            <tr>
              <th className="p-4">Código</th>
              <th className="p-4">Colaborador</th>
              <th className="p-4">Cargo / Sede</th>
              <th className="p-4">Jefe Inmediato</th>
              <th className="p-4 text-center">Resultado</th>
              <th className="p-4 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100/80">
            {cargando ? (
              <tr><td colSpan="6" className="p-16 text-center text-slate-500 font-bold animate-pulse">Cargando actas de compromiso...</td></tr>
            ) : actasPermitidas.length === 0 ? (
              <tr><td colSpan="6" className="p-16 text-center text-slate-500 font-bold">No hay actas registradas para tu sede</td></tr>
            ) : (
              actasPermitidas.map(acta => (
                <tr key={acta.id} className="hover:bg-white/80 transition">
                  <td className="p-4 font-mono font-black text-indigo-700">{acta.codigo_formato || 'PF-TH-101'}</td>
                  <td className="p-4 font-black text-slate-900">{acta.nombre_empleado}</td>
                  <td className="p-4 text-slate-600 font-medium">
                    <span className="block font-bold text-slate-800">{acta.cargo_empleado || 'Operativo'}</span>
                    <span className="text-[10px] text-slate-400">Bodega {acta.sede_id || 'N/A'}</span>
                  </td>
                  <td className="p-4 font-bold text-slate-700">{acta.jefe_inmediato || 'N/A'}</td>
                  <td className="p-4 text-center">
                    <span className={`px-3 py-1 rounded-full text-[10px] font-black ${
                      acta.resultado_final === 'CUMPLIDO' ? 'bg-emerald-100 text-emerald-800' :
                      acta.resultado_final === 'NO_CUMPLIDO' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                    }`}>
                      {acta.resultado_final || 'EN_SEGUIMIENTO'}
                    </span>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex justify-center items-center gap-2">
                      {(permisosCRUD?.editar || alcanceABAC.esGlobal) && (
                        <button onClick={() => abrirModalEditar(acta)} className="px-3 py-1.5 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-700 font-black text-[11px] cursor-pointer transition border border-blue-200">
                          ✏️ Editar / Firmar
                        </button>
                      )}
                      {(permisosCRUD?.eliminar || alcanceABAC.esGlobal) && (
                        <button onClick={() => handleEliminar(acta.id)} className="px-3 py-1.5 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 font-black text-[11px] cursor-pointer transition border border-rose-200">
                          🗑️ Eliminar
                        </button>
                      )}
                      {!permisosCRUD?.editar && !permisosCRUD?.eliminar && !alcanceABAC.esGlobal && (
                        <span className="text-[10px] text-slate-400 font-bold italic">Solo lectura</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalAbierto && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white/90 backdrop-blur-xl rounded-[32px] p-6 sm:p-8 max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-white animate-fade-in custom-scrollbar">
            <div className="flex justify-between items-center border-b border-slate-200/60 pb-4 mb-6">
              <div>
                <span className="font-mono text-xs font-black text-indigo-700 bg-indigo-50 px-3 py-1 rounded-lg border border-indigo-100">CÓDIGO: PF-TH-101</span>
                <h3 className="text-xl font-black text-slate-900 mt-1">
                  {actaEditando ? 'Editar Acta / Firmar en Diferido' : 'Nueva Acta de Compromiso y Plan de Mejora'}
                </h3>
              </div>
              <button onClick={() => setModalAbierto(false)} className="w-8 h-8 rounded-full bg-slate-100 font-bold text-slate-600 hover:bg-slate-200 cursor-pointer">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 bg-white/60 p-5 rounded-2xl border border-white shadow-sm">
                <div className="sm:col-span-2">
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Colaborador</label>
                  <select required value={form.id_empleado} onChange={(e) => handleSelectEmpleado(e.target.value)} className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 shadow-inner">
                    <option value="">Selecciona trabajador...</option>
                    {empleadosPermitidos.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.codigo_interno} — {emp.nombre_completo} ({emp.empl_cargo})</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Jefe Inmediato</label>
                  <input type="text" value={form.jefe_inmediato} onChange={(e) => setForm({ ...form, jefe_inmediato: e.target.value })} className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 shadow-inner" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Responsable Seguimiento</label>
                  <input type="text" value={form.responsable_seguimiento} onChange={(e) => setForm({ ...form, responsable_seguimiento: e.target.value })} placeholder="Ej: Talento Humano" className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 shadow-inner" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Fecha Seguimiento Inicial</label>
                  <input type="date" value={form.fecha_seguimiento_inicial} onChange={(e) => setForm({ ...form, fecha_seguimiento_inicial: e.target.value })} className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-800 shadow-inner" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-black text-slate-800 uppercase mb-2">Motivo del Compromiso / Plan de Mejora</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-white/60 p-5 rounded-2xl border border-white shadow-sm">
                  {MOTIVOS_LISTA.map(motivo => (
                    <label key={motivo} className="flex items-center gap-2.5 text-xs font-bold text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={!!form.motivos[motivo]} onChange={(e) => setForm({ ...form, motivos: { ...form.motivos, [motivo]: e.target.checked } })} className="w-4 h-4 text-blue-600 rounded border-slate-300" />
                      <span>{motivo}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white/60 p-5 rounded-2xl border border-white shadow-sm">
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-2">Hechos, novedades o antecedentes:</label>
                  <textarea rows="4" value={form.hechos_antecedentes} onChange={(e) => setForm({ ...form, hechos_antecedentes: e.target.value })} className="w-full p-3 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-800 shadow-inner" placeholder="Describa la situación..." />
                </div>
                <div className="bg-white/60 p-5 rounded-2xl border border-white shadow-sm flex flex-col gap-2">
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Verificación Previa (Soportes/Conocimiento):</label>
                  {['perfil_cargo', 'manual_funciones', 'induccion', 'reinduccion', 'procedimiento_especifico', 'dotacion'].map(campo => (
                    <div key={campo} className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-700 capitalize">{campo.replace('_', ' ')}</span>
                      <select 
                        value={form.verificacion_previa[campo]} 
                        onChange={(e) => setForm({ ...form, verificacion_previa: { ...form.verificacion_previa, [campo]: e.target.value } })}
                        className="p-1 bg-white border border-slate-200 rounded-lg text-[10px] font-bold shadow-inner"
                      >
                        <option value="No aplica">No aplica</option>
                        <option value="Sí">Sí</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-white/60 p-5 rounded-2xl border border-white shadow-sm">
                  <label className="block text-[10px] font-black text-slate-500 uppercase mb-2">Versión del Trabajador:</label>
                  <textarea rows="2" value={form.version_trabajador} onChange={(e) => setForm({ ...form, version_trabajador: e.target.value })} className="w-full p-3 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-800 shadow-inner" placeholder="Escriba aquí la versión del trabajador..." />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-xs font-black text-slate-800 uppercase">Compromisos del Trabajador</label>
                    <button type="button" onClick={() => setForm({ ...form, compromisos_trabajador: [...form.compromisos_trabajador, { accion: '', indicador: '', plazo: '' }] })} className="px-3 py-1.5 bg-slate-900 text-white rounded-xl text-xs font-black cursor-pointer shadow-sm">+ Añadir Fila</button>
                  </div>
                  <div className="space-y-2">
                    {form.compromisos_trabajador.map((comp, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <input type="text" placeholder="Acción concreta..." value={comp.accion} onChange={(e) => { const arr = [...form.compromisos_trabajador]; arr[idx].accion = e.target.value; setForm({ ...form, compromisos_trabajador: arr }); }} className="flex-2 p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-medium shadow-inner" />
                        <input type="text" placeholder="Indicador/Evidencia" value={comp.indicador} onChange={(e) => { const arr = [...form.compromisos_trabajador]; arr[idx].indicador = e.target.value; setForm({ ...form, compromisos_trabajador: arr }); }} className="flex-1 p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-medium shadow-inner" />
                        <input type="text" placeholder="Plazo" value={comp.plazo} onChange={(e) => { const arr = [...form.compromisos_trabajador]; arr[idx].plazo = e.target.value; setForm({ ...form, compromisos_trabajador: arr }); }} className="w-28 p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-medium shadow-inner" />
                        <button type="button" onClick={() => setForm({ ...form, compromisos_trabajador: form.compromisos_trabajador.filter((_, i) => i !== idx) })} className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg font-black cursor-pointer">✕</button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <label className="block text-xs font-black text-slate-800 uppercase">Plan de Seguimiento y Resultado</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-white/60 p-5 rounded-2xl border border-white shadow-sm">
                  {form.seguimientos.map((seg, i) => (
                    <div key={i} className="bg-white p-4 rounded-xl border border-slate-200 space-y-2.5 shadow-xs">
                      <span className="font-black text-[11px] text-blue-700 block">Seguimiento #{seg.etapa}</span>
                      <input type="date" value={seg.fecha} onChange={(e) => { const arr = [...form.seguimientos]; arr[i].fecha = e.target.value; setForm({ ...form, seguimientos: arr }); }} className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-xs font-medium shadow-inner" />
                      <input type="text" placeholder="Aspecto verificado..." value={seg.aspecto} onChange={(e) => { const arr = [...form.seguimientos]; arr[i].aspecto = e.target.value; setForm({ ...form, seguimientos: arr }); }} className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-xs font-medium shadow-inner" />
                      <input type="text" placeholder="Resultado..." value={seg.resultado} onChange={(e) => { const arr = [...form.seguimientos]; arr[i].resultado = e.target.value; setForm({ ...form, seguimientos: arr }); }} className="w-full p-2.5 bg-white border border-slate-200 rounded-lg text-xs font-medium shadow-inner" />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div>
                    <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Resultado Final</label>
                    <select value={form.resultado_final} onChange={(e) => setForm({ ...form, resultado_final: e.target.value })} className="w-full p-3 bg-white border border-slate-200 rounded-xl text-xs font-black text-slate-800 shadow-inner">
                      <option value="EN_SEGUIMIENTO">⏳ En Seguimiento</option>
                      <option value="CUMPLIDO">✅ Cumplido</option>
                      <option value="CUMPLIMIENTO_PARCIAL">⚠️ Cumplimiento Parcial</option>
                      <option value="NO_CUMPLIDO">❌ No Cumplido</option>
                      <option value="REQUIERE_EXTENSION">📋 Requiere Extensión</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-500 uppercase mb-1">Observaciones Finales</label>
                    <input type="text" value={form.observaciones_finales} onChange={(e) => setForm({ ...form, observaciones_finales: e.target.value })} className="w-full p-3 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-800 shadow-inner" placeholder="Conclusiones..." />
                  </div>
                </div>
              </div>

              <div className="bg-slate-50/80 p-5 rounded-2xl border border-slate-200">
                <label className="block text-[10px] font-black text-slate-600 uppercase mb-3 text-center tracking-widest bg-slate-200/50 py-1.5 rounded-lg">
                  Panel de Firmas a Mano (Se pueden realizar en diferido)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  <FirmaPad 
                    titulo="Firma Trabajador" 
                    valorBase64={form.firmas.trabajador} 
                    onGuardarFirma={(val) => setForm({ ...form, firmas: { ...form.firmas, trabajador: val } })}
                    onLimpiar={() => setForm({ ...form, firmas: { ...form.firmas, trabajador: '' } })}
                  />
                  <FirmaPad 
                    titulo="Jefe Inmediato" 
                    valorBase64={form.firmas.jefe} 
                    onGuardarFirma={(val) => setForm({ ...form, firmas: { ...form.firmas, jefe: val } })}
                    onLimpiar={() => setForm({ ...form, firmas: { ...form.firmas, jefe: '' } })}
                  />
                  <FirmaPad 
                    titulo="Talento Humano" 
                    valorBase64={form.firmas.th} 
                    onGuardarFirma={(val) => setForm({ ...form, firmas: { ...form.firmas, th: val } })}
                    onLimpiar={() => setForm({ ...form, firmas: { ...form.firmas, th: '' } })}
                  />
                  <FirmaPad 
                    titulo="Testigo (Opcional)" 
                    valorBase64={form.firmas.testigo1} 
                    onGuardarFirma={(val) => setForm({ ...form, firmas: { ...form.firmas, testigo1: val } })}
                    onLimpiar={() => setForm({ ...form, firmas: { ...form.firmas, testigo1: '' } })}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200/60">
                <button type="button" onClick={() => setModalAbierto(false)} className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-xs cursor-pointer transition">Cancelar</button>
                <button type="submit" disabled={guardando} className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black text-xs shadow-md cursor-pointer transition">
                  {guardando ? 'Guardando en BD...' : 'Guardar Acta PF-TH-101'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}